import { useEffect, useState } from "react";
import { Sunrise, Moon, Sparkles, PartyPopper, Thermometer, Droplets, Sun, ChevronDown, Sunset } from "lucide-react";
import { api } from "@/lib/api";

// Compact time: "9:00 AM" -> "9", "8:30 AM" -> "8:30", "10:00 PM" -> "10".
const short = (t) => (t ? t.replace(/:00/, "").replace(/\s?(AM|PM)/i, "") : t);

// UV index -> risk label + colour (WHO scale) so a guide can see sun risk at a glance.
const uvLevel = (uv) => {
  if (uv == null) return null;
  if (uv <= 2) return { label: "Low", cls: "text-emerald-400" };
  if (uv <= 5) return { label: "Mod", cls: "text-yellow-400" };
  if (uv <= 7) return { label: "High", cls: "text-orange-400" };
  if (uv <= 10) return { label: "V.High", cls: "text-red-400" };
  return { label: "Extreme", cls: "text-fuchsia-400" };
};

// Today's WDW park hours (4 main parks) — a tidy 2-up grid that fills the row.
export const ParkHoursCard = () => {
  const [parks, setParks] = useState(null);
  const [weather, setWeather] = useState(null);
  const [showHourly, setShowHourly] = useState(false);

  useEffect(() => {
    let active = true;
    api.getParkHours()
      .then((d) => { if (!active) return; setParks(d.parks || []); setWeather(d.weather || null); })
      .catch(() => active && setParks([]));
    return () => { active = false; };
  }, []);

  if (!parks || parks.length === 0) return null;
  const uv = weather ? uvLevel(weather.uv) : null;

  return (
    <div
      className="rounded-2xl border border-zinc-700 bg-zinc-900/60 px-3 py-3"
      data-testid="park-hours-card"
    >
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-[11px] font-800 uppercase tracking-wider text-zinc-400">Today at WDW</h2>
        <span className="text-[10px] uppercase tracking-wide text-zinc-600">Park hours</span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {parks.map((p) => (
          <div
            key={p.short}
            className="flex min-w-0 flex-col justify-center rounded-xl border border-zinc-800 bg-zinc-950/50 px-3 py-2"
            data-testid={`park-hours-${p.short}`}
          >
            <div className="flex items-baseline gap-2">
              <span className="text-[11px] font-800 tracking-wide text-[#E0575B]">{p.short}</span>
              {p.closed ? (
                <span className="text-sm font-600 text-zinc-500">Closed</span>
              ) : (
                <span className="text-[15px] font-800 leading-none text-zinc-50">{short(p.open)}–{short(p.close)}</span>
              )}
            </div>
            {!p.closed && (p.early_open || p.extended_close || p.party_open || p.fireworks) && (
              <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] leading-none">
                {p.early_open && (
                  <span className="inline-flex items-center gap-0.5 text-amber-400/90" title={`Early Entry ${p.early_open}`} data-testid={`park-early-${p.short}`}>
                    <Sunrise className="h-2.5 w-2.5" />{short(p.early_open)}
                  </span>
                )}
                {p.fireworks && (
                  <span className="inline-flex items-center gap-0.5 font-700 text-sky-300/90" title={`Fireworks ${p.fireworks}`} data-testid={`park-fireworks-${p.short}`}>
                    <Sparkles className="h-2.5 w-2.5" />Fireworks {short(p.fireworks)}
                  </span>
                )}
                {p.extended_close && (
                  <span className="inline-flex items-center gap-0.5 text-indigo-300/90" title={`Extended Evening til ${p.extended_close}`} data-testid={`park-extended-${p.short}`}>
                    <Moon className="h-2.5 w-2.5" />{short(p.extended_close)}
                  </span>
                )}
                {p.party_open && (
                  <span className="inline-flex items-center gap-0.5 font-700 text-fuchsia-400/90" title={`Special ticketed event ${p.party_open}–${p.party_close}`} data-testid={`park-party-${p.short}`}>
                    <PartyPopper className="h-2.5 w-2.5" />Party {short(p.party_open)}–{short(p.party_close)}
                  </span>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      {weather && (weather.high != null || weather.rain_chance != null || weather.uv != null) && (
        <div className="mt-2 border-t border-zinc-800 pt-2">
          <button
            onClick={() => (weather.hourly || []).length > 0 && setShowHourly((v) => !v)}
            className={`flex w-full flex-wrap items-center gap-x-2.5 gap-y-1 text-[12px] font-600 leading-none text-zinc-300 ${(weather.hourly || []).length > 0 ? "cursor-pointer" : "cursor-default"}`}
            data-testid="park-weather"
          >
            {(weather.high != null || weather.low != null) && (
              <span className="inline-flex items-center gap-1" data-testid="weather-temp">
                <Thermometer className="h-3.5 w-3.5 text-[#E0575B]" />
                <span className="text-zinc-50">{weather.high != null ? `${weather.high}°` : "—"}</span>
                <span className="text-zinc-500">/{weather.low != null ? `${weather.low}°` : "—"}</span>
              </span>
            )}
            {weather.feels_like != null && (
              <span className="text-orange-300" data-testid="weather-feels">Feels {weather.feels_like}°</span>
            )}
            {weather.rain_chance != null && (
              <span className="inline-flex items-center gap-1 text-sky-300" data-testid="weather-rain">
                <Droplets className="h-3.5 w-3.5" />{weather.rain_chance}%
              </span>
            )}
            {uv && (
              <span className={`inline-flex items-center gap-1 ${uv.cls}`} data-testid="weather-uv">
                <Sun className="h-3.5 w-3.5" />UV {weather.uv} · {uv.label}
              </span>
            )}
            {weather.sunset && (
              <span className="inline-flex items-center gap-1 text-amber-300/90" data-testid="weather-sunset">
                <Sunset className="h-3.5 w-3.5" />{weather.sunset}
              </span>
            )}
            {(weather.hourly || []).length > 0 && (
              <ChevronDown className={`ml-auto h-3.5 w-3.5 shrink-0 text-zinc-500 transition-transform ${showHourly ? "rotate-180" : ""}`} />
            )}
          </button>
          {showHourly && (weather.hourly || []).length > 0 && (
            <div className="mt-2 flex gap-1.5 overflow-x-auto pb-1" data-testid="weather-hourly">
              {weather.hourly.map((h, i) => (
                <div key={i} className="flex min-w-[44px] shrink-0 flex-col items-center gap-1 rounded-lg border border-zinc-800 bg-zinc-950/60 px-1.5 py-1.5" data-testid={`weather-hour-${i}`}>
                  <span className="text-[9px] font-700 uppercase tracking-wide text-zinc-500">{h.time}</span>
                  <span className="text-[13px] font-800 text-zinc-50">{h.temp != null ? `${h.temp}°` : "—"}</span>
                  <span className={`inline-flex items-center gap-0.5 text-[10px] font-600 ${h.rain >= 40 ? "text-sky-300" : "text-zinc-500"}`}>
                    <Droplets className="h-2.5 w-2.5" />{h.rain != null ? `${h.rain}%` : "—"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
